/**
 * Repositorio local-first sobre IndexedDB.
 *
 * Sprint 4: el frontend escribe SIEMPRE a IDB primero (instantaneo,
 * funciona offline). El sync engine drena la cola contra el server
 * cuando hay conectividad y la key AES esta en memoria.
 *
 * Sin key AES (sesion fresca pos-tab-close) los reads fallan con
 * `OfflineError('no_key')` — el caller fallback a network o muestra
 * "Reabre sesion online".
 */
import type { AssessmentDetail } from '../api/assessments.js';
import { getCryptoKey } from '../auth/crypto-key.js';
import { decryptBytes, decryptJson, encryptBytes, encryptJson } from './crypto.js';
import { type LocalPendingChange, type LocalPhotoQueueItem, offlineDB } from './db.js';

export class OfflineError extends Error {
  constructor(public readonly reason: 'no_db' | 'no_key' | 'not_found') {
    super(`OfflineError: ${reason}`);
    this.name = 'OfflineError';
  }
}

function requireDb() {
  if (!offlineDB) throw new OfflineError('no_db');
  return offlineDB;
}

function requireKey(): CryptoKey {
  const k = getCryptoKey();
  if (!k) throw new OfflineError('no_key');
  return k;
}

function newId(): string {
  // crypto.randomUUID disponible en navegadores modernos + Node 19+
  return crypto.randomUUID();
}

// ─────────────────────────────────────────────────────────────────────
// Assessments
// ─────────────────────────────────────────────────────────────────────

export async function saveAssessment(a: AssessmentDetail): Promise<void> {
  const db = requireDb();
  const key = requireKey();
  const blob = await encryptJson(key, a);
  await db.assessments.put({
    id: a.id,
    iv: blob.iv,
    ciphertext: blob.ciphertext,
    updatedAt: Date.now(),
  });
}

export async function loadAssessment(id: string): Promise<AssessmentDetail | null> {
  const db = requireDb();
  const key = requireKey();
  const row = await db.assessments.get(id);
  if (!row) return null;
  return decryptJson<AssessmentDetail>(key, { iv: row.iv, ciphertext: row.ciphertext });
}

export async function patchLocalResponses(
  assessmentId: string,
  instanceId: string,
  responses: Record<string, unknown>,
): Promise<void> {
  const a = await loadAssessment(assessmentId);
  if (!a) throw new OfflineError('not_found');
  const next: AssessmentDetail = {
    ...a,
    instances: a.instances.map((i) =>
      i.id === instanceId ? { ...i, responses, status: 'in_progress' } : i,
    ),
  };
  await saveAssessment(next);
}

// ─────────────────────────────────────────────────────────────────────
// Templates cache
// ─────────────────────────────────────────────────────────────────────

export async function cacheTemplate(code: string, version: number, schema: unknown): Promise<void> {
  const db = requireDb();
  const key = requireKey();
  const blob = await encryptJson(key, schema);
  await db.templates.put({
    key: `${code}@${String(version)}`,
    iv: blob.iv,
    ciphertext: blob.ciphertext,
    cachedAt: Date.now(),
  });
}

export async function loadCachedTemplate(code: string, version: number): Promise<unknown> {
  const db = requireDb();
  const key = requireKey();
  const row = await db.templates.get(`${code}@${String(version)}`);
  if (!row) return null;
  return decryptJson(key, { iv: row.iv, ciphertext: row.ciphertext });
}

// ─────────────────────────────────────────────────────────────────────
// Pending changes queue
// ─────────────────────────────────────────────────────────────────────

export async function enqueueResponsesChange(input: {
  assessmentId: string;
  instanceId: string;
  responses: Record<string, unknown>;
  /** S15-C · Notas del auditor por pregunta. Opcionales. */
  auditorNotes?: Record<string, string>;
}): Promise<string> {
  const db = requireDb();
  const key = requireKey();
  const id = newId();
  const blob = await encryptJson(key, {
    instance_id: input.instanceId,
    responses: input.responses,
    ...(input.auditorNotes !== undefined ? { auditor_notes: input.auditorNotes } : {}),
  });
  await db.pending.add({
    clientChangeId: id,
    kind: 'responses',
    assessmentId: input.assessmentId,
    iv: blob.iv,
    ciphertext: blob.ciphertext,
    ts: Date.now(),
    status: 'pending',
    attempts: 0,
  });
  return id;
}

export async function enqueueCompleteChange(assessmentId: string): Promise<string> {
  const db = requireDb();
  const key = requireKey();
  const id = newId();
  const blob = await encryptJson(key, {});
  await db.pending.add({
    clientChangeId: id,
    kind: 'complete',
    assessmentId,
    iv: blob.iv,
    ciphertext: blob.ciphertext,
    ts: Date.now(),
    status: 'pending',
    attempts: 0,
  });
  return id;
}

export async function listPending(): Promise<LocalPendingChange[]> {
  const db = requireDb();
  return db.pending.where('status').equals('pending').toArray();
}

export async function markChangeSyncing(clientChangeId: string): Promise<void> {
  const db = requireDb();
  await db.pending.update(clientChangeId, { status: 'syncing' });
}

export async function markChangeApplied(clientChangeId: string): Promise<void> {
  const db = requireDb();
  await db.pending.delete(clientChangeId);
}

export async function markChangeError(clientChangeId: string, error: string): Promise<void> {
  const db = requireDb();
  const existing = await db.pending.get(clientChangeId);
  if (!existing) return;
  await db.pending.update(clientChangeId, {
    status: 'pending',
    attempts: existing.attempts + 1,
    lastError: error,
  });
}

export async function countPending(): Promise<number> {
  const db = offlineDB;
  if (!db) return 0;
  return db.pending.where('status').equals('pending').count();
}

// ─────────────────────────────────────────────────────────────────────
// Photo queue
// ─────────────────────────────────────────────────────────────────────

export async function enqueuePhoto(input: {
  assessmentId: string;
  instanceId: string;
  questionRef: string;
  file: Blob;
  contentType: string;
  capturedAt?: number;
}): Promise<LocalPhotoQueueItem> {
  const db = requireDb();
  const key = requireKey();
  const id = newId();
  const bytes = new Uint8Array(await input.file.arrayBuffer());
  const blob = await encryptBytes(key, bytes);
  const item: LocalPhotoQueueItem = {
    id,
    assessmentId: input.assessmentId,
    instanceId: input.instanceId,
    questionRef: input.questionRef,
    iv: blob.iv,
    ciphertext: blob.ciphertext,
    contentType: input.contentType,
    capturedAt: input.capturedAt ?? Date.now(),
    status: 'pending',
  };
  await db.photos.add(item);
  return item;
}

export async function listPendingPhotos(): Promise<LocalPhotoQueueItem[]> {
  const db = requireDb();
  return db.photos.where('status').equals('pending').toArray();
}

export async function loadPhotoBlob(id: string): Promise<Blob | null> {
  const db = requireDb();
  const key = requireKey();
  const row = await db.photos.get(id);
  if (!row) return null;
  const bytes = await decryptBytes(key, { iv: row.iv, ciphertext: row.ciphertext });
  return new Blob([bytes as unknown as BlobPart], { type: row.contentType });
}

export async function markPhotoUploaded(id: string, serverPhotoId: string): Promise<void> {
  const db = requireDb();
  await db.photos.update(id, { status: 'uploaded', serverPhotoId });
}

export async function markPhotoError(id: string, error: string): Promise<void> {
  const db = requireDb();
  await db.photos.update(id, { status: 'error', lastError: error });
}

export async function countPendingPhotos(): Promise<number> {
  const db = offlineDB;
  if (!db) return 0;
  return db.photos.where('status').equals('pending').count();
}

// ─────────────────────────────────────────────────────────────────────
// Wipe (logout o cierre de sesion)
// ─────────────────────────────────────────────────────────────────────

export async function wipeLocal(): Promise<void> {
  if (!offlineDB) return;
  await Promise.all([
    offlineDB.assessments.clear(),
    offlineDB.photos.clear(),
    offlineDB.pending.clear(),
    offlineDB.templates.clear(),
  ]);
}
